/**
 * Other viewers of a World, as the conductor draws them: a cursor that
 * glides to the slot a peer points at, the ring of bits a peer aims a carry
 * at, and their names and hues. Presence frames move them; a peer silent for
 * fifteen seconds loses its cursor and aim, since its socket may have
 * dropped mid-drag with no `end`. Pure: time and the field's geometry come in.
 */

import type { Cue } from '../contracts';
import { hashHue } from './palette';

type Pt = { x: number; y: number };
type Rect = { x: number; y: number; w: number; h: number };

export interface Peer {
  hue: number;
  name: string | null;
  cursor: { world: string; key: number; slot: number } | null;
  /** Where the cursor is drawn now (stage px), gliding toward its cell. */
  shown: Pt | null;
  aim: { world: string; key: number; slots: bigint; label: string } | null;
  lastTrail: number;
  lastAt: number;
}

export const PEER_TTL_MS = 15_000;

/** A mask from a frame's slots ("0x600" or [9, 10]); null when it reads as neither. */
export type MaskOf = (v: unknown) => bigint | null;

export interface PeerField {
  /** Where a slot's cell is drawn, in stage px; null when it is not laid out. */
  cellRect(world: string, key: number, slot: number): Rect | null;
  /** The World (the frame's own, else any on stage) and key a date names, when it is on screen. */
  find(world: string | undefined, date?: string, hour?: number): { world: string; key: number } | null;
}

export class PeerLayer {
  private readonly peers = new Map<string, Peer>();

  constructor(private readonly maskOf: MaskOf) {}

  get(sid: string): Peer | undefined {
    return this.peers.get(sid);
  }

  all(): IterableIterator<[string, Peer]> {
    return this.peers.entries();
  }

  of(sid: string, clock: number): Peer {
    let p = this.peers.get(sid);
    if (!p) {
      p = { hue: hashHue(sid), name: null, cursor: null, shown: null, aim: null, lastTrail: 0, lastAt: clock };
      this.peers.set(sid, p);
    }
    return p;
  }

  set(sid: string, info: { hue?: number; name?: string }, clock: number): void {
    const p = this.of(sid, clock);
    if (info.hue !== undefined) p.hue = info.hue;
    if (info.name !== undefined) p.name = info.name;
  }

  forget(sid: string): void {
    this.peers.delete(sid);
  }

  /** A peer's presence frame: where it points, what it aims at, or that its carry ended. */
  hear(cue: Extract<Cue, { kind: 'peer' }> & { world?: string }, clock: number, field: PeerField): void {
    const p = this.of(cue.sid, clock);
    p.lastAt = clock;
    const f = cue.frame;
    if (f.t === 'at') {
      const at = f.slot !== undefined ? field.find(cue.world, f.date, f.hour) : null;
      p.cursor = at && f.slot !== undefined ? { ...at, slot: f.slot } : null;
      if (!p.cursor) p.shown = null;
    } else if (f.t === 'lift' || f.t === 'over') {
      const at = field.find(cue.world, f.date, f.hour);
      const slots = this.maskOf(f.slots);
      const label = f.t === 'lift' ? f.label : p.aim?.label ?? '';
      p.aim = at && slots !== null && slots !== 0n ? { ...at, slots, label } : f.t === 'lift' ? null : p.aim;
    } else if (f.t === 'end') {
      p.aim = null;
    }
  }

  /** A peer silent for PEER_TTL_MS loses its cursor and aim. */
  expire(clock: number): void {
    for (const p of this.peers.values()) {
      if ((p.aim || p.cursor) && clock - p.lastAt >= PEER_TTL_MS) {
        p.aim = null;
        p.cursor = null;
        p.shown = null;
      }
    }
  }

  /** Cursors glide toward their cells; `trail` leaves a fading mark behind a moving one. */
  follow(dt: number, clock: number, reduced: boolean, field: PeerField, trail: (at: Pt, p: Peer) => void): void {
    for (const p of this.peers.values()) {
      if (!p.cursor) continue;
      const r = field.cellRect(p.cursor.world, p.cursor.key, p.cursor.slot);
      if (!r) continue;
      const tx = r.x + r.w / 2, ty = r.y + r.h / 2;
      if (!p.shown || reduced) p.shown = { x: tx, y: ty };
      else {
        const k = 1 - Math.exp(-dt / 70);
        const nx = p.shown.x + (tx - p.shown.x) * k, ny = p.shown.y + (ty - p.shown.y) * k;
        if (clock - p.lastTrail > 30 && Math.hypot(nx - p.shown.x, ny - p.shown.y) > 0.5) {
          p.lastTrail = clock;
          trail(p.shown, p);
        }
        p.shown = { x: nx, y: ny };
      }
    }
  }

  /** Anything of theirs still moving: an aim ring breathes, a cursor not yet at its cell glides. */
  busy(reduced: boolean, field: PeerField): boolean {
    if (reduced) return false;
    for (const p of this.peers.values()) {
      if (p.aim) return true;   // the aim ring breathes (on the sea's clock, which stands still when reduced)
      if (p.cursor) {
        const r = field.cellRect(p.cursor.world, p.cursor.key, p.cursor.slot);
        if (r && (!p.shown || Math.hypot(r.x + r.w / 2 - p.shown.x, r.y + r.h / 2 - p.shown.y) > 0.3)) return true;
      }
    }
    return false;
  }

  /** When a peer pointing or aiming will be let go, if it stays silent. */
  wakeAt(t: number): number {
    let at = Infinity;
    for (const p of this.peers.values()) if (p.aim || p.cursor) at = Math.min(at, Math.max(t, p.lastAt + PEER_TTL_MS));
    return at;
  }
}
